import { inject, injectable } from 'inversify'
import getDecorators from 'inversify-inject-decorators'
import { isNil } from 'lodash-es'
import type { FilterStrategyInterface } from '@/services/tables/filters/strategies/types/filterStrategyInterface'
import type { FilterField } from '@/services/tables/filters/fields/filterField'
import { FilterOperationEnum, type FilterOperationValue, type Operation } from '@/services/types'
import { SERVICE_TYPES } from '@/core/container/types'
import { TmTableFilterError } from '@/core/error/table/tmTableFilterError'
import {
  isTFilterRange,
  isTFilterRangeEnd,
  isTFilterRangeFull,
  isTFilterRangeStart,
  type TFilterRange,
} from '@/services/filters/types'
import type { BaseFilterStrategy } from '@/services/tables/filters/strategies/baseFilterStrategy'
import type { ParsedFilterType } from '@/services/tables/types'
import type { FilterStrategyGetPopulateDataResult } from '@/services/tables/filters/strategies/types/filterStrategyGetPopulateDataResult'
import type { RelativeDateTimeService } from '@/services/datetime/relativeDateTimeService'
import { isTRelativeDateTimeRange, type TDateTimeRange } from '@/services/datetime/types'
import type DateTimeService from '@/services/dateTimeService'
import { getContainer } from '@/core/container/container'
import type { SelectOption } from '@/services/forms/types'

type DateRangeOperation = Extract<Operation, 'eq' | 'gte' | 'gt' | 'lte' | 'lt' | 'isNull'>

const { lazyInject } = getDecorators(getContainer())

const toRangeEdge = (value: unknown): string => String(value ?? '')

@injectable()
export class RangeFilterStrategy implements FilterStrategyInterface {
  // lazyInject is used because to RelativeDateTimeService is in the dateTime.module
  @lazyInject(SERVICE_TYPES.RelativeDateTimeService)
  protected readonly relativeDateTimeService: RelativeDateTimeService

  // lazyInject is used because to DateTimeService is in the dateTime.module
  @lazyInject(SERVICE_TYPES.DateTimeService)
  protected readonly dateTimeService: DateTimeService

  private readonly _filterValueCache = new WeakMap<{ name: string }, { value: unknown; range: TDateTimeRange }>()

  private _filterKeys: Record<string, { name: string }> = {}

  constructor(@inject(SERVICE_TYPES.BaseFilterStrategy) protected readonly baseFilterStrategy: BaseFilterStrategy) {}

  public toQueryPart(filter: FilterField): Partial<FilterOperationValue> {
    const relationField = filter.getRelationField()

    if (filter.operation === 'isEmpty') {
      return this.baseFilterStrategy.fieldDataToQueryPart({
        operation: filter.operation,
        relationField,
        value: filter.value,
      })
    }

    const value = filter.getApiValue()
    if (!isTFilterRange(value)) {
      throw new TmTableFilterError(`Incorrect range value "${String(value)}" for filter "${filter.getName()}"`)
    }

    ;[value.startOperation, value.endOperation].filter(Boolean).forEach((op) => {
      if (!filter.availableOperations.includes(op as Operation)) {
        throw new TmTableFilterError(`Operation "${op}" is not available for filter "${filter.getName()}"`)
      }
    })

    if (isTFilterRangeFull(value)) {
      return {
        ...this.baseFilterStrategy.fieldDataToQueryPart({
          operation: value.startOperation,
          relationField,
          value: value.start,
        }),
        ...this.baseFilterStrategy.fieldDataToQueryPart({
          operation: value.endOperation,
          relationField,
          value: value.end,
        }),
      }
    }

    if (isTFilterRangeStart(value)) {
      return this.baseFilterStrategy.fieldDataToQueryPart({
        operation: value.startOperation,
        relationField,
        value: value.start,
      })
    }

    if (isTFilterRangeEnd(value)) {
      return this.baseFilterStrategy.fieldDataToQueryPart({
        operation: value.endOperation,
        relationField,
        value: value.end,
      })
    }

    throw new TmTableFilterError(`Incorrect range value "${value}" for filter "${filter.getName()}"`)
  }

  public getPopulateData(
    filter: FilterField,
    payload: ParsedFilterType[],
    selectedItems?: SelectOption[],
  ): FilterStrategyGetPopulateDataResult {
    const rangeStartOperations: Set<Operation> = new Set(['gt', 'gte', 'isNull'])
    const rangeEndOperations: Set<Operation> = new Set(['lt', 'lte'])

    const filteredPayload = payload.filter((item) => filter.matchesPayloadEntry(item))

    const isEmptyPayload = filteredPayload.find((item) => item.operation === 'isEmpty' && item.value !== null)

    if (isEmptyPayload) {
      // If no new selectedItems provided, preserve current values from the filter
      const currentSelectedItemsValue = filter.selectedItemsValue ?? []
      const currentInnerSelectedItemsValue = filter.innerSelectedItemsValue ?? []
      const hasNewSelectedItems = selectedItems && selectedItems.length > 0

      return {
        isApplied: true,
        operation: 'isEmpty',
        innerOperation: 'isEmpty',
        value: isEmptyPayload.value,
        innerValue: isEmptyPayload.value,
        selectedItemsValue: hasNewSelectedItems ? [...selectedItems] : [...currentSelectedItemsValue],
        innerSelectedItemsValue: hasNewSelectedItems ? [...selectedItems] : [...currentInnerSelectedItemsValue],
      }
    }

    const range = payload
      .filter((item) => filter.matchesPayloadEntry(item))
      .reduce<TFilterRange>((accum, item) => {
        if (isNil(item.value) || item.value === '') {
          return accum
        }

        // Do not overwrite existing non-empty values in accum
        if (rangeStartOperations.has(item.operation) && !(accum.start && accum.startOperation)) {
          accum.start = toRangeEdge(item.value)
          accum.startOperation = item.operation
        }

        // Do not overwrite existing non-empty values in accum
        if (rangeEndOperations.has(item.operation) && !(accum.end && accum.endOperation)) {
          accum.end = toRangeEdge(item.value)
          accum.endOperation = item.operation
        }

        // Overwrite existing non-empty values in accum
        if (item.operation === FilterOperationEnum.isEmpty) {
          accum.start = undefined
          accum.startOperation = undefined
          accum.end = undefined
          accum.endOperation = undefined

          accum[FilterOperationEnum.isEmpty] = item.value
        }

        return accum
      }, {} as TFilterRange)

    if (!isTFilterRange(range)) {
      return null
    }

    const rangeValue = {
      start: range.start,
      startOperation: range.startOperation,
      end: range.end,
      endOperation: range.endOperation,
    } as TFilterRange

    // If no new selectedItems provided, preserve current values from the filter
    const currentSelectedItemsValue = filter.selectedItemsValue ?? []
    const currentInnerSelectedItemsValue = filter.innerSelectedItemsValue ?? []
    const hasNewSelectedItems = selectedItems && selectedItems.length > 0

    return {
      isApplied: true,
      operation: 'eq',
      innerOperation: 'eq',
      value: rangeValue,
      innerValue: rangeValue,
      selectedItemsValue: hasNewSelectedItems ? [...selectedItems] : [...currentSelectedItemsValue],
      innerSelectedItemsValue: hasNewSelectedItems ? [...selectedItems] : [...currentInnerSelectedItemsValue],
    }
  }

  public checkFilterValue(filter: FilterField, dt: string | Date): boolean {
    const value = filter.getApiValue()
    if (!value) {
      return true
    }
    if (!this.relativeDateTimeService.isValidDate(dt)) {
      return true
    }
    if (!(dt instanceof Date)) {
      dt = this.dateTimeService.toDate(dt)
    }
    let range: TDateTimeRange
    try {
      const cacheKey = this.getCachedFilterKey(filter)
      const cachedValue = this._filterValueCache.get(cacheKey)
      if (value === cachedValue?.value && cachedValue?.range) {
        range = cachedValue.range
      } else if (!isTRelativeDateTimeRange(value)) {
        return true
      } else {
        range = this.relativeDateTimeService.getDateRange(value)
        this._filterValueCache.set(cacheKey, { value, range })
      }
      const startDate = range.start
      const endDate = range.end
      if (!this.checkDate(range.startOperation as DateRangeOperation, dt, startDate)) {
        return false
      }
      if (!this.checkDate(range.endOperation as DateRangeOperation, dt, endDate)) {
        return false
      }
      return true
    } catch {
      return false
    }
  }

  private checkDate(operation: DateRangeOperation, date: Date, rangeDate: Date): boolean {
    if (operation === 'isNull' && !date) {
      return true
    }
    if (operation === 'eq' && date.getTime() !== rangeDate.getTime()) {
      return false
    }
    if (operation === 'gte' && date.getTime() < rangeDate.getTime()) {
      return false
    }
    if (operation === 'gt' && date.getTime() <= rangeDate.getTime()) {
      return false
    }
    if (operation === 'lte' && date.getTime() > rangeDate.getTime()) {
      return false
    }
    if (operation === 'lt' && date.getTime() >= rangeDate.getTime()) {
      return false
    }
    return true
  }

  private getCachedFilterKey(filter: FilterField): { name: string } {
    if (!this._filterKeys[filter.name]) {
      this._filterKeys[filter.name] = { name: filter.name }
    }
    return this._filterKeys[filter.name]
  }
}
